// Data Persistence System for Omatech Digital
// This system ensures user data survives across deployments and site updates

class DataPersistence {
  constructor() {
    this.storageKey = 'omatech_persistent_data';
    this.backupKey = 'omatech_data_backup';
    this.version = '1.0.0';
    this.init();
  }

  init() {
    // Migrate existing data if needed
    this.migrateExistingData();
    
    // Create backup of current data
    this.createBackup();
    
    // Set up periodic backups
    this.setupPeriodicBackup();
    
    console.log('🔄 Data persistence system initialized');
  }

  // Migrate data from old localStorage keys to new system
  migrateExistingData() {
    const oldKeys = ['om_users', 'om_reqs', 'om_session'];
    const migratedData = {};
    let hasOldData = false;

    oldKeys.forEach(key => {
      try {
        const oldData = localStorage.getItem(key);
        if (oldData) {
          migratedData[key] = JSON.parse(oldData);
          hasOldData = true;
          console.log(`✅ Migrated ${key} data`);
        }
      } catch (error) {
        console.warn(`⚠️ Failed to migrate ${key}:`, error);
      }
    });

    if (hasOldData) {
      // Store migrated data in new system
      this.setDataInternal('migrated_data', migratedData);
      console.log('✅ Data migration completed');
    }
  }

  // Get data with fallback to backup
  getData(key, defaultValue = null) {
    try {
      // Try to get from main storage
      const data = localStorage.getItem(`${this.storageKey}_${key}`);
      if (data) {
        return JSON.parse(data);
      }

      // Fallback to backup storage
      const backupData = localStorage.getItem(`${this.backupKey}_${key}`);
      if (backupData) {
        const parsed = JSON.parse(backupData);
        // Restore from backup
        this.setDataInternal(key, parsed);
        console.log(`🔄 Restored ${key} from backup`);
        return parsed;
      }

      // Try to get from old localStorage keys for backward compatibility
      const oldKey = `om_${key}`;
      const oldData = localStorage.getItem(oldKey);
      if (oldData) {
        const parsed = JSON.parse(oldData);
        // Migrate to new system
        this.setDataInternal(key, parsed);
        console.log(`🔄 Migrated ${key} from old storage`);
        return parsed;
      }

      return defaultValue;
    } catch (error) {
      console.error(`❌ Error getting data for ${key}:`, error);
      return defaultValue;
    }
  }

  // Internal setData method (no recursive calls)
  setDataInternal(key, value) {
    try {
      const dataString = JSON.stringify(value);
      localStorage.setItem(`${this.storageKey}_${key}`, dataString);
      
      // Also update backup
      localStorage.setItem(`${this.backupKey}_${key}`, dataString);
      
      return true;
    } catch (error) {
      console.error(`❌ Error setting data for ${key}:`, error);
      return false;
    }
  }

  // Public setData method
  setData(key, value) {
    const success = this.setDataInternal(key, value);
    
    // Update last modified timestamp (avoiding recursive call)
    if (success && key !== 'last_modified') {
      this.setDataInternal('last_modified', Date.now());
    }
    
    return success;
  }

  // Remove data
  removeData(key) {
    try {
      localStorage.removeItem(`${this.storageKey}_${key}`);
      localStorage.removeItem(`${this.backupKey}_${key}`);
      return true;
    } catch (error) {
      console.error(`❌ Error removing data for ${key}:`, error);
      return false;
    }
  }

  // Create backup of all data
  createBackup() {
    try {
      const allData = {};
      const keys = ['users', 'reqs', 'session', 'migrated_data'];
      
      keys.forEach(key => {
        const data = this.getData(key);
        if (data) {
          allData[key] = data;
        }
      });

      // Create timestamped backup
      const backupData = {
        timestamp: Date.now(),
        version: this.version,
        data: allData
      };

      localStorage.setItem('omatech_full_backup', JSON.stringify(backupData));
      console.log('✅ Full backup created');
      return true;
    } catch (error) {
      console.error('❌ Backup creation failed:', error);
      return false;
    }
  }

  // Restore from backup
  restoreFromBackup() {
    try {
      const backupString = localStorage.getItem('omatech_full_backup');
      if (!backupString) {
        console.warn('⚠️ No backup found');
        return false;
      }

      const backup = JSON.parse(backupString);
      const { data } = backup;

      // Restore all data
      Object.keys(data).forEach(key => {
        this.setDataInternal(key, data[key]);
      });

      console.log('✅ Data restored from backup');
      return true;
    } catch (error) {
      console.error('❌ Backup restoration failed:', error);
      return false;
    }
  }

  // Export data for manual backup
  exportData() {
    try {
      const allData = {};
      const keys = ['users', 'reqs', 'session'];
      
      keys.forEach(key => {
        const data = this.getData(key);
        if (data) {
          allData[key] = data;
        }
      });

      const exportData = {
        timestamp: Date.now(),
        version: this.version,
        data: allData
      };

      // Create downloadable file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `omatech_backup_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);

      console.log('✅ Data exported successfully');
      return true;
    } catch (error) {
      console.error('❌ Data export failed:', error);
      return false;
    }
  }

  // Import data from file
  importData(jsonData) {
    try {
      const imported = JSON.parse(jsonData);
      const { data } = imported;

      if (!data || typeof data !== 'object') {
        throw new Error('Invalid data format');
      }

      // Import all data
      Object.keys(data).forEach(key => {
        this.setDataInternal(key, data[key]);
      });

      console.log('✅ Data imported successfully');
      return true;
    } catch (error) {
      console.error('❌ Data import failed:', error);
      return false;
    }
  }

  // Setup periodic backup every 5 minutes
  setupPeriodicBackup() {
    setInterval(() => {
      this.createBackup();
    }, 5 * 60 * 1000); // 5 minutes
  }

  // Get data statistics
  getDataStats() {
    try {
      const stats = {
        users: 0,
        reqs: 0,
        hasSession: false,
        lastBackup: null,
        storageSize: 0
      };

      // Count users
      const users = this.getData('users', []);
      stats.users = users.length;

      // Count requests
      const reqs = this.getData('reqs', []);
      stats.reqs = reqs.length;

      // Check session
      const session = this.getData('session');
      stats.hasSession = !!session;

      // Get backup info
      const backupString = localStorage.getItem('omatech_full_backup');
      if (backupString) {
        const backup = JSON.parse(backupString);
        stats.lastBackup = new Date(backup.timestamp).toLocaleString();
      }

      // Calculate storage size
      stats.storageSize = new Blob([JSON.stringify(this.getAllData())]).size;

      return stats;
    } catch (error) {
      console.error('❌ Error getting data stats:', error);
      return null;
    }
  }

  // Get all data
  getAllData() {
    const allData = {};
    const keys = ['users', 'reqs', 'session', 'migrated_data'];
    
    keys.forEach(key => {
      const data = this.getData(key);
      if (data) {
        allData[key] = data;
      }
    });

    return allData;
  }

  // Clear all data (use with caution!)
  clearAllData() {
    try {
      const keys = ['users', 'reqs', 'session', 'migrated_data'];
      
      keys.forEach(key => {
        this.removeData(key);
      });

      // Clear backups
      localStorage.removeItem('omatech_full_backup');
      localStorage.removeItem('omatech_data_backup');

      console.log('⚠️ All data cleared');
      return true;
    } catch (error) {
      console.error('❌ Error clearing data:', error);
      return false;
    }
  }

  // Force data migration from old system
  forceMigration() {
    console.log('🔄 Forcing data migration...');
    this.migrateExistingData();
    return true;
  }

  // Check if data exists in any storage
  hasData(key) {
    const mainData = localStorage.getItem(`${this.storageKey}_${key}`);
    const backupData = localStorage.getItem(`${this.backupKey}_${key}`);
    const oldData = localStorage.getItem(`om_${key}`);
    
    return !!(mainData || backupData || oldData);
  }
}

// Create global instance
const dataPersistence = new DataPersistence();

// Export for use in components
export default dataPersistence;

// Also export individual functions for backward compatibility
export const {
  getData,
  setData,
  removeData,
  createBackup,
  restoreFromBackup,
  exportData,
  importData,
  getDataStats,
  getAllData,
  clearAllData
} = dataPersistence;
